<template>
	<div style='width: 100%;'>
		<Banner v-if="banner" :banner="banner" />
		<div 
			class="session-card__wrapper" 
			style="padding: 16px 0;"
			v-bind:style="{marginTop: banner ? '8px' : '0'}"
		>
			<ProfileBadge 
				v-if="agendaEvent.sponsored_by"
				isPin
				:pin="{icon: sponsoredStarIcon, text: sponsoredLabel}"
			/>

			<!-- text -->
			<div 
				v-if='agendaEvent.events_agenda_text' 
				class="session-card-title mb-4 px-4"
				v-bind:style="{paddingTop: agendaEvent.sponsored_by ? '14px' : '0'}"
			>
				{{ agendaEvent.events_agenda_text }}
			</div>

			<!-- date time -->
			<div 
				v-if='agendaEvent.events_agenda_from && agendaEvent.events_agenda_to' 
				class="session-card-date mb-2 px-4"
			>
				{{ formatDateRange(agendaEvent.events_agenda_from, agendaEvent.events_agenda_to, agendaEvent.agendaTimezone) }}
			</div>

			<!-- location -->
			<div class="session-card-location mb-2 px-4" v-if="agendaEvent.session_location">
				<Icon 
					type="pin" 
					:size="10" 
					color="var(--location-color)" 
					:pr="8" 
				/>
				{{ agendaEvent.session_location }}
			</div>


			<!-- tracks -->
			<div 
				class='mb-4 px-4' 
				v-if='agendaEvent.tracks.length > 0' 
			>
				<div 
					v-for="{ track_color, track_id, track_name } in agendaEvent.tracks" 
					:key="track_id"
					class="session-card-tag mb-1" 
					:style="`color: ${track_color}`"
				>
					<v-icon :color="track_color" dark small class="mr-1"> mdi-circle</v-icon>
					{{ track_name }}
				</div>
			</div>

			<div class="session-card-registered mb-4 px-4" v-if="agendaEvent.is_registered">
				<v-chip class="session-card-registered-chip">
					Registered
				</v-chip>
			</div>

			<!-- description -->
			<div class="session-card-text mb-4 px-4" 
				v-if='agendaEvent.session_description'
				v-html='agendaEvent.session_description' 
			/>

			<v-divider />

			<div class="session-card-add-my-agenda-wrapper">
				<v-btn 
					class="session-card-add-my-agenda-btn elevation-0" 
					v-bind:style="{ color: myAgendaButton.color }"
					@click="toggleMyAgenda" 
					:loading="isMyAgendaLoading"
				>
					<span class="session-card-add-my-agenda-btn-text">
						{{ myAgendaButton.text }}
					</span>
				</v-btn>
			</div>
		</div>
		<!-- speakers -->
		<div class="session-card__wrapper" v-if='agendaEvent.speakers.length > 0'>
			<h4 class='session-card-title mb-4'>
				Speaker(s)
			</h4>
			<router-link 
				:to="{
					name: 'speaker-by-id',
					params: { id: +speaker_id }
				}"
				v-for="{ speaker_logo, speaker_name, speaker_title, speaker_id } in agendaEvent.speakers.slice(0, 3)"
				:key="speaker_id"
				class="session-card-speaker"
			>
				<Avatar 
					class="mr-8 post-preview-card__avatar" 
					size="50" 
					:avatar="speaker_logo"
				/>
				<p v-if='speaker_name' class="session-card-speaker-name">
					{{ speaker_name }}
				</p>
				<Icon 
					type="chevron_right"
					color="#9B9B9B"
					:size="18"
				/>
			</router-link>

			<div class="session-card__link-wrapper">
				<!-- v-if='agendaEvent.speakers.length >= 3' -->
				<router-link 
					:to="{
						name: 'event-session-speakers',
						params: {
							eventId: agendaEvent.events_id,
							sessionId: agendaEvent.events_agenda_id
						}
					}"
					class="session-card__link pt-2"
				>
					Show All
				</router-link>
			</div>
		</div>

		<div class="session-card__wrapper" v-if='agendaEvent.uploads.length > 0'>
			<!-- files -->
			<h4 class='session-card-title mb-3'>Files</h4>

			<div v-if="agendaEvent.uploads && agendaEvent.uploads.length">
				<div
					class="d-flex flex-column mx-4"
					v-for="attachment in agendaEvent.uploads.slice(0, 3)" 
					:key="attachment.upload_id"
				>
					<SessionAttachments 
						class="file_box" 
						:attachment="attachment" 
					/>
				</div>
			</div>

			<div 
				class='session-card__link-wrapper mt-3 text-center'
				v-if='agendaEvent.uploads.length >= 3'
			>
				<router-link 
					:to="{
						name: 'event-session-files',
						params: {
							eventId: this.$route.params.eventId,
							sessionId: this.$route.params.sessionId
						}
					}" 
					class="session-card__link text-decoration-none"
				>
					Show All
				</router-link>
			</div>
		</div>
		<div class="session-card__wrapper" v-if="agendaEvent.session_qa_is_available">
			<!-- questions -->
				<h4 class='session-card-title mb-4'>
					Top 3 Session Question(s)
				</h4>
				<div v-if="agendaEvent.session_question_answers.length">
					<div 
						v-for="question in agendaEvent.session_question_answers"
						:key="question.session_question_answer_id"
						class="session-card__question"
					>
						<span class="session-card__question-text">
							{{ question.session_question_answer_text }}
						</span>
						<span 
							:class="{
								'session-card__question-count d-flex align-center': true, 
								'session-card__question-count-active': question.upvote
							}"
						>
							<Icon 
								type="like" 
								:size="16" 
								:color="question.upvote ? 'var(--button-main-background-color)' : '#697583'" 
								:pr="5" 
							/>
							{{ question.session_question_count }}
						</span>
					</div>
				</div>
				<div class="session-card__link-wrapper">
					<router-link :to="{ name: 'event-session-questions' }">
						<v-btn v-if="!agendaEvent.session_question_answers.length"
							class="session-card__button elevation-0">
							Add a Question
						</v-btn>
						<v-btn v-else class="session-card__button elevation-0">
							View More/Add
						</v-btn>
					</router-link>
				</div>

		</div>

		<div class="session-card__wrapper" v-if="polls.length">
			<h4 class='session-card-title'>
				Survey(s)
			</h4>
			<Poll 
				v-for="poll in polls" 
				:key="poll.poll_id" 
				:poll="poll" 
				:answers="polls_answers"
				:isEventVoteDirty="isVoteDirty" 
				:pollsLoading="pollsLoading"
				@answerChange="answerChange" 
			/>
			<v-btn 
				v-if="!isPollsVoted" 
				class="event-card__poll-btn"
				:class="{ 'event-card__poll-btn-disabled': voteDisabled }" 
				:ripple="false" 
				@click="handleVote"
				:loading="pollsLoading"
			>
				Vote
			</v-btn>
		</div>

		<div class="session-card__wrapper" v-if="ratings.length">
			<h4 class='session-card-title'>
				Rating(s)
			</h4>
			<Rating 
				v-for="rating in ratings" 
				:key="rating.rating_id" 
				:rating="rating"
				@ratingChange="ratingChange" 
			/>
			<v-btn 
				v-if="isRatingsAvailable" 
				class="event-card__rating-btn"
				:class="{ 'event-card__rating-btn-disabled': ratingDisabled }" 
				:ripple="false" 
				@click="handleRate"
				:loading="ratingsLoading"
			>
				Send Rating
			</v-btn>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Avatar from '@/components/Avatar.vue'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import Poll from '@/components/Post/Poll'
import Rating from '@/components/Event/Rating'
import ProfileBadge from '@/components/ProfileBadge.vue'
import Banner from '@/components/Banner'
import { mapActions, mapGetters } from 'vuex'

Vue.use(VueScrollTo)

export default {
	name: 'SessionCard',
	components: {
		SessionAttachments,
		Avatar,
		Poll,
		Rating,
		ProfileBadge,
		Banner
	},
	props: {
		agendaEvent: {
			type: Object,
			default: () => { },
		},
	},
	data() {
		return {
			sponsoredStarIcon,
			polls_answers: [],
			isVoteDirty: false,
			rating_answers: []
		}
	},

	methods: {
		...mapActions('event', ['addMyAgenda', 'removeMyAgenda', 'voteEventPolls', 'voteSessionRatings']),

		formatDateRange(dateFrom, dateTo, timezone) {
			const options = {
				weekday: 'long',
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC'
			}

			//Convert Unix timestamps to JavaScript Date objects
			const fromDate = new Date(dateFrom * 1000)
			const toDate = new Date(dateTo * 1000)
			const timezoneOffset = Number(timezone.timezone_utc.slice(-2))
			const action = timezone.timezone_utc.slice(3, 4)
			if (action === '-') {
				fromDate.setHours(fromDate.getHours() - timezoneOffset)
				toDate.setHours(toDate.getHours() - timezoneOffset)
			} else {
				fromDate.setHours(fromDate.getHours() + timezoneOffset)
				toDate.setHours(toDate.getHours() + timezoneOffset)
			}

			// Format the dates
			const formattedFromDate = fromDate.toLocaleString('en-US', options)
			const formattedToDate = toDate.toLocaleTimeString('en-US', options)

			// Construct the formatted date range string
			return `${formattedFromDate} - ${formattedToDate}`
		},
		toggleMyAgenda() {
			if (!this.isMyAgendaLoading) {
				if (this.agendaEvent.is_scheduled) {
					this.removeMyAgenda({ id: this.agendaEvent.events_agenda_id, myAgenda: false, single: true })
				} else {
					this.addMyAgenda({ id: this.agendaEvent.events_agenda_id, single: true })
				}
			}
		},
		answerChange(answer) {
			if (!answer.answer || (Array.isArray(answer.answer) && !answer.answer.length)) {
				if (this.polls_answers.length) {
					this.polls_answers = this.polls_answers.filter(poll => poll.poll_id !== answer.poll_id)
				}
			} else {
				const pollAnswer = this.polls_answers.find(poll => poll.poll_id === answer.poll_id)
				if (pollAnswer) {
					this.polls_answers = this.polls_answers.map(poll => {
						if (poll.poll_id === answer.poll_id) {
							return answer
						} else {
							return poll
						}
					})
				} else {
					this.polls_answers = [...this.polls_answers, answer]
				}
			}
		},
		async handleVote() {
			this.isVoteDirty = true
			if (!this.voteDisabled) {
				await this.voteEventPolls({ answers: this.polls_answers, eventId: null, sessionId: this.agendaEvent.events_agenda_id })
			}
		},
		ratingChange(rating) {
			if (this.rating_answers.find(item => item.rating_id === rating.rating_id)) {
				this.rating_answers = this.rating_answers.map(item => {
					if (item.rating_id === rating.rating_id) {
						return {
							...rating
						}
					} else {
						return item
					}
				})
			} else {
				this.rating_answers = [...this.rating_answers, rating]
			}
		},
		async handleRate() {
			if (!this.ratingDisabled) {
				await this.voteSessionRatings({ ratings: this.rating_answers, sessionId: +this.$route.params.sessionId })
			}
		}
	},
	computed: {
		...mapGetters('event', ['isMyAgendaLoading', 'pollsLoading', 'ratingsLoading']),
		...mapGetters('advertisement', ['banner']),

		polls() {
			return this.agendaEvent.polls.filter(poll => poll.pollsAnswers.length || (!poll.pollsAnswers.length && poll.allow_custom_answer))
		},
		voteDisabled() {
			return this.pollsLoading || this.polls_answers.length !== this.polls.length || this.polls_answers.some(poll => poll.error)
		},
		isPollsVoted() {
			return this.agendaEvent.polls.every(poll => poll.vote)
		},
		sponsoredLabel() {
			return this.agendaEvent.sponsored_by ? `Sponsored by ${this.agendaEvent.sponsored_by}` : ''
		},
		myAgendaButton() {
			return this.agendaEvent.is_scheduled ?
				{ text: 'Remove from My Agenda', color: '#9B9B9B', icon: 'calendar_check' }
				:
				{ text: 'Add to My Agenda', color: 'var(--accent-color)', icon: 'calendar_star' }
		},
		ratings() {
			return this.agendaEvent.ratings.filter(rating => !rating.vote || (rating.vote && rating.display_rating))
		},
		isRatingsAvailable() {
			return this.ratings.some(rating => !rating.vote)
		},
		availableRatings() {
			return this.ratings.filter(rating => !rating.vote)
		},
		ratingDisabled() {
			return this.rating_answers.length !== this.availableRatings.length
		}
	}
}
</script>

<style lang="scss">
.session-card {
	font-family: Montserrat, sans-serif;
	&__wrapper {
		position: relative;
		background-color: #fff;
		padding: 16px 16px;
		border-radius: 10px;
		margin-bottom: 8px;
	}

	&-date {
		color: var(--accent-color);
		font-size: 14px;
		font-weight: 700;
		line-height: 17.07px;
	}

	&-title {
		color: var(--accent-color);
		font-size: 16px;
		font-weight: 700;
		line-height: 19.5px;
	}

	&-text {
		color: var(--accent-color);
		font-size: 14px;
		line-height: 17.07px;

		p {
			margin: 0;
		}
	}

	&-tag {
		display: flex;
		align-items: center;
		font-size: 13px;
		font-weight: 700;
		line-height: 19.37px;
	}

	&-location {
		display: flex;
		align-items: center;
		color: var(--button-main-background-color);
		font-size: 13px;
		font-weight: 500;
		line-height: 19.37px;
	}

	&-speaker {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		text-decoration: none;
		background-color: #F3F3F6;
		padding: 16px;
		border-radius: 12px;

		&:not(:last-child) {
			margin-bottom: 8px;
		}

		&-name {
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			color: var(--accent-color);
			margin: 0 !important;
			flex-grow: 1;
		}
	}

	&__link {
		font-size: 14px;
		font-weight: 700;
		line-height: 17.07px;
		color: var(--button-main-background-color) !important;

		&-wrapper {
			display: flex;
			justify-content: center;
		}
	}

	&__button {
		font-size: 14px;
		font-weight: 700;
		line-height: 17.07px;
		color: var(--button-main-background-color) !important;
		background-color: transparent !important;
		text-transform: capitalize;
		text-decoration: underline;
		margin-top: 7px;
	}

	&-registered {
		padding: 0 30px;

		&-chip {
			height: 23px !important;
			font-size: 12px !important;
			line-height: 14.63px;
			font-weight: 700;
			background-color: var(--accent-color) !important;
			color: #fff !important;
		}
	}

	&-add-my-agenda {
		&-wrapper {
			padding: 20px 16px 4px 16px;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}

		&-btn {
			text-transform: none;
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			color: #0055A3;
			background-color: transparent !important;
			padding: 0 8px !important;

			&-text {
				text-decoration: underline;
			}
		}
	}

	&__add-question-btn,
	&__add-question-btn-view {
		background: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;
		text-transform: capitalize;
		font-size: 16px !important;
		font-weight: 700;
	}

	&__add-question-btn-view {
		color: var(--accent-color) !important;
		background: transparent !important;
	}

	&__question {
		display: flex;
		flex-wrap: nowrap;
		justify-content: space-between;
		padding: 17.5px 16px;
		background-color: #F3F3F6;
		border-radius: 12px;
		
		&:not(:last-child) {
			margin-bottom: 8px;
		}

		&-text {
			font-size: 14px;
			font-weight: 700;
			line-height: 17.07px;
			color: var(--accent-color);
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}

		&-count {
			font-size: 13px;
			font-weight: 500;
			line-height: 19.37px;
			color: #697583;

			&-active {
				color: var(--button-main-background-color);
			}
		}
	}
}

.file_box {
	background: #F3F3F6;
	padding: 12px;
	border-radius: 12px;
	gap: 12px;
}
</style>
